// frontend/app/features.js
// Single source of truth for UI feature flags.
// Order of precedence (lowest → highest):
// defaults → (optional) .env JSON → localStorage → URL ?ff_* overrides

const DEFAULTS = {
  ask_dock: false,           // ⬅️ Floating mic + input (replaces bottombar)
  club_mode: true,          // ⬅️ hides chrome, big dock, chips, big tiles // BDD 10/17/2025
  confetti_on_quiz: true,   // ⬅️ confetti overlay on quiz submit
  utility_drawer: false,     // Example from plan (replaces always-on sidebar)
  primer_deepen: true,      // Tabs on Answer card
  collections_mvp: true,    // Tiles + progress
  primer_stream_cancel: false,
  auto_cycle: false,
  // 10/8/2025 - demo mode
  investor_mode: false,           // main switch for demo flow
  demo_preset_id: "investor_2025_10_14", // label/version; can override via ?ff_demo_preset_id=
  // BDD 10/14/2025
  // ⬇ NEW: hard override for the waiting-trivia overlay
  // false  => force OFF
  // true   => force ON
  // (omit or set to null/undefined to defer to demo_preset.json)
  show_trivia_when_waiting: false,
  ask_agent: false, // BDD FG 10/17/2025
  show_continue_button: false, // BDD 01/25/2026 studen resum states
  curriculum_tile_view: true, // BDD 10/17/2025,
  native_iqh3d_video: false, // BDD 10/20/2025 play video in WebView; flip to true to try native 3D HLS later
  healthcheck: false, // BDD 02/16/2026 - internal PWO WebGL healthcheck UI
  homeworkHelp: true, // BDD 02/21/2026 HomeWorkHelpMVP
  ai_stream: true, // BDD 04/12/2026 - stream through /api/ask_ai itself

  // PWO / WebGL performance
  // - `pwo_keepalive`: keep Unity runtime alive on close (hides iframe, no src blank)
  // - `pwo_hot_switch`: when Unity is already ready, switch PWOs by postMessage without reloading iframe
  // - `pwo_hot_switch_max`: after N hot-switches, force a cold reboot to avoid long-session slowdowns
  // - `pwo_args_cache_ms`: cache `/get_pwo_arguments_webgl` responses in-memory for this many ms
  pwo_keepalive: true,
  pwo_hot_switch: true,
  pwo_hot_switch_max: 6,
  pwo_args_cache_ms: 120000,

  // BDD 10/01/2026 - projector-wide SBS 3D mode.
  // Enable per-session with ?ff_sbs3d=1 (feature overrides persist via features.js).
  sbs3d: false,
  // BDD 10/02/2026 - Windows/Edge kiosk owns fullscreen externally.
  // In this mode AITutor must not call the browser Fullscreen API.
  // Enable with ?ff_sbs3d=1&ff_sbs3d_kiosk=1.
  sbs3d_kiosk: false,
  // Delay between handing CamMode=3D_0 to Unity and revealing the Unity framebuffer.
  // This masks the unsynchronized WebGL/Unity startup frame in projector SBS mode.
  // Override per session, e.g. ?ff_sbs3d_pwo_handoff_ms=3500
  sbs3d_pwo_handoff_ms: 2500,
  // pwo_webgl_gate: true,
  // BDD 04/16/2026 - local llm
  ai_mode: "cloud_realtime", // local_nvidia
  ai_latency_overlay: true,
  ai_local_voice: false,
  ai_local_tts: false,
  ai_local_asr: false,
  ai_local_actions: false,

  // Lesson in a Box 2026-08-13 - lesson-plan chip on topic tiles + lesson runtime.
  // Ships dark; enable per-session with ?ff_lesson_plan=1
  lesson_plan: false,

  // BDD 10/04/2026 - authored Chapter Learning Path / interactive textbook.
  // Ships dark. Enable per-session with ?ff_chapter_learning_path=1.
  // When disabled, the existing Chapter -> Topic tile experience is unchanged.
  chapter_learning_path: false,
  // BDD 08/20/2026 - AI EXHIBIT RELATED - AI Explained museum kiosk
  ai_exhibit: false,
  ai_exhibit_v2: false,          // BDD 09/16/2026 - Nick/Kanchan React V2 integration
  ai_exhibit_require_login: false,
  ai_exhibit_voice: false,
  ai_exhibit_open_ask: false,


  // Saras Learning Loop 2026-08-24 - the seven-step loop, mastery bands,
  // TeachSense and Discovery Journeys.
  //
  // The seven-phase rail itself is NOT gated here: it comes from the lesson's
  // own `schema_version`, so a v2 lesson renders seven steps whatever this is
  // set to. What the flag gates is the new *surfaces* — the Discovery Journeys
  // entry, the TeachSense sections of the Insights panel, and the student
  // mastery map — so the shipped five-phase demo keeps working on dev while
  // this lands. Enable per-session with ?ff_learning_loop=1
  learning_loop: false,

};

// OPTIONAL: if you ever want to feed flags at build time, you can set a JSON string
// in your frontend/.env as REACT_APP_FEATURES or EXPO_PUBLIC_FEATURES.
// If not set, this is simply ignored.
function readEnvJSON() {
  try {
    const raw =
      (typeof process !== "undefined" && process.env && (
        process.env.REACT_APP_FEATURES || process.env.EXPO_PUBLIC_FEATURES
      )) || null;
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function readLocal() {
  try {
    const raw = localStorage.getItem("s3d_features");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeLocal(obj) {
  try {
    localStorage.setItem("s3d_features", JSON.stringify(obj));
  } catch {}
}

function readUrlOverrides() {
  try {
    const u = typeof window !== "undefined" ? new URL(window.location.href) : null;
    if (!u) return {};
    const p = u.searchParams;

    // Convention: any param that starts with ff_ maps to a flag name
    // Example: ?ff_ask_dock=0  or  ?ff_ask_dock=1
    const overrides = {};
    p.forEach((val, key) => {
      if (key.startsWith("ff_")) {
        const name = key.slice(3); // strip "ff_"
        // normalize boolean-ish values
        if (val === "1" || val.toLowerCase() === "true" || val.toLowerCase() === "on") {
          overrides[name] = true;
        } else if (val === "0" || val.toLowerCase() === "false" || val.toLowerCase() === "off") {
          overrides[name] = false;
        } else {
          // allow raw string if you ever want non-bool flags
          overrides[name] = val;
        }
      }
    });

    // Helper: ?ff_reset=1 clears localStorage flags (handy while testing)
    if (p.get("ff_reset") === "1") writeLocal({});

    // If URL provided any ff_*, persist for future reloads
    if (Object.keys(overrides).length) {
      const merged = { ...readLocal(), ...overrides };
      writeLocal(merged);
    }
    return overrides;
  } catch {
    return {};
  }
}

export function getFeatureFlags() {
  const env = readEnvJSON();
  const local = readLocal();
  const url = readUrlOverrides();
  // Precedence: defaults → env → local → url
  return { ...DEFAULTS, ...env, ...local, ...url };
}

// React-friendly hook (memoized)
import { useMemo } from "react";
export function useFeatureFlags() {
  // Recompute on each mount; if you need live updates, you could watch storage events
  return useMemo(() => getFeatureFlags(), []);
}
